import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
import { useCallback, useEffect, useRef, useState } from 'react';
import glue from '../core/glue';
import runtimeVar from '../core/runtimeVar';
import subscribe from '../core/subscribe';
import { isDevelopment, isModel } from '../tools';
import useCloneModel from './internalHooks/useCloneModel';
import useService from './internalHooks/useService';
import { defaultServiceOptions } from '../core/constants';
var idAtom = 0;
var useModel = function useModel(initState, service, deps, options) {
  var finalOptions = _objectSpread(_objectSpread({}, defaultServiceOptions), options);
  var _useState = useState(function () {
      if (isModel(initState)) return initState;
      if (typeof initState === 'function') {
        return glue(null, initState());
      }
      return glue(initState);
    }),
    _useState2 = _slicedToArray(_useState, 1),
    model = _useState2[0];
  if (isModel(initState) && !Object.is(model, initState)) {
    model = initState;
  }
  var _useState3 = useState(function () {
      var b = idAtom;
      idAtom += 1;
      return {
        id: b
      };
    }),
    _useState4 = _slicedToArray(_useState3, 1),
    idState = _useState4[0];
  var optionsRef = useRef(finalOptions);
  optionsRef.current = finalOptions;
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    updateState = _useState6[1];
  var subscribeCallback = useCallback(function (_modelData) {
    updateState({});
  }, []);
  var modelRef = useRef();
  var offChangeRef = useRef();
  var offUpdateRef = useRef();
  var unsubRef = useRef();
  var callbackIdsRef = useRef([]);
  var sta = model();
  var cachedOnChangeState = useRef(sta);
  var cachedOnUpdateState = useRef(sta);
  var onChangeCallback = useCallback(function (state) {
    if (optionsRef.current.onChange) {
      var current = cachedOnChangeState.current;
      cachedOnChangeState.current = state;
      optionsRef.current.onChange(state, current);
    }
  }, []);
  var onUpdateCallback = useCallback(function (state) {
    if (optionsRef.current.onUpdate) {
      var current = cachedOnUpdateState.current;
      cachedOnUpdateState.current = state;
      optionsRef.current.onUpdate(state, current);
    }
  }, []);
  var modelBind = function modelBind() {
    var _offChangeRef$current, _offUpdateRef$current, _unsubRef$current, _offChangeRef$current2, _offUpdateRef$current2;
    (_offChangeRef$current = offChangeRef.current) === null || _offChangeRef$current === void 0 ? void 0 : _offChangeRef$current.call(offChangeRef);
    (_offUpdateRef$current = offUpdateRef.current) === null || _offUpdateRef$current === void 0 ? void 0 : _offUpdateRef$current.call(offUpdateRef);
    (_unsubRef$current = unsubRef.current) === null || _unsubRef$current === void 0 ? void 0 : _unsubRef$current.call(unsubRef);
    callbackIdsRef.current.splice(0);
    runtimeVar.runtimeBindType = 1;
    offChangeRef.current = model.onChange(onChangeCallback);
    offUpdateRef.current = model.onUpdate(onUpdateCallback);
    callbackIdsRef.current.push((_offChangeRef$current2 = offChangeRef.current) === null || _offChangeRef$current2 === void 0 ? void 0 : _offChangeRef$current2.__id, (_offUpdateRef$current2 = offUpdateRef.current) === null || _offUpdateRef$current2 === void 0 ? void 0 : _offUpdateRef$current2.__id);
    runtimeVar.runtimeBindType = 0;
    unsubRef.current = subscribe([model], subscribeCallback, false, true);
    modelRef.current = model;
  };
  if (!Object.is(modelRef.current, model)) {
    modelBind();
  }
  var _useCloneModel = useCloneModel(model, subscribeCallback, finalOptions),
    _useCloneModel2 = _slicedToArray(_useCloneModel, 2),
    clonedModel = _useCloneModel2[0],
    status = _useCloneModel2[1];
  runtimeVar.runtimeUpdateOrigin = {
    updateType: 1,
    callbackIds: _toConsumableArray(callbackIdsRef.current)
  };
  runtimeVar.runtimeUpdateOriginId = idState.id;
  var _useService = useService(model, clonedModel, service, deps, finalOptions),
    _useService2 = _slicedToArray(_useService, 1),
    localService = _useService2[0];
  runtimeVar.runtimeUpdateOrigin = null;
  runtimeVar.runtimeUpdateOriginId = null;
  useEffect(function () {
    if (isDevelopment()) {
      modelBind();
    }
  }, [model]);
  useEffect(function () {
    return function () {
      var _unsubRef$current2, _offChangeRef$current3, _offUpdateRef$current3;
      (_unsubRef$current2 = unsubRef.current) === null || _unsubRef$current2 === void 0 ? void 0 : _unsubRef$current2.call(unsubRef);
      (_offChangeRef$current3 = offChangeRef.current) === null || _offChangeRef$current3 === void 0 ? void 0 : _offChangeRef$current3.call(offChangeRef);
      (_offUpdateRef$current3 = offUpdateRef.current) === null || _offUpdateRef$current3 === void 0 ? void 0 : _offUpdateRef$current3.call(offUpdateRef);
    };
  }, []);
  return [model(), clonedModel, _objectSpread(_objectSpread({}, status), {}, {
    service: localService
  })];
};
export default useModel;